import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Crown, ArrowLeft, Loader2, Medal, Star, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { championshipsApi } from '../lib/api';
import { AlboLegacyManager } from '../components/AlboLegacyManager';

const isItalianLang = (lang) => lang === 'it' || !lang;

const PODIUM = {
  1: {
    rank: '1°',
    label: { it: 'Campione', en: 'Champion' },
    color: '#FFD700',
    badge: 'bg-[#FFD700]/15 border-[#FFD700]/50 text-[#FFD700]',
    ring: 'border-[#FFD700]/60',
    glow: 'shadow-[0_0_60px_rgba(255,215,0,0.25)]',
    base: 'from-[#FFD700]/30 to-transparent',
    baseH: 'h-10 sm:h-14',
    icon: Crown,
  },
  2: {
    rank: '2°',
    label: { it: 'Argento', en: 'Silver' },
    color: '#C0C0C0',
    badge: 'bg-[#C0C0C0]/15 border-[#C0C0C0]/50 text-[#C0C0C0]',
    ring: 'border-[#C0C0C0]/50',
    glow: 'shadow-[0_0_35px_rgba(192,192,192,0.15)]',
    base: 'from-[#C0C0C0]/25 to-transparent',
    baseH: 'h-6 sm:h-9',
    icon: Medal,
  },
  3: {
    rank: '3°',
    label: { it: 'Bronzo', en: 'Bronze' },
    color: '#CD7F32',
    badge: 'bg-[#CD7F32]/15 border-[#CD7F32]/50 text-[#CD7F32]',
    ring: 'border-[#CD7F32]/50',
    glow: 'shadow-[0_0_35px_rgba(205,127,50,0.15)]',
    base: 'from-[#CD7F32]/25 to-transparent',
    baseH: 'h-4 sm:h-6',
    icon: Medal,
  },
};

const ClubLogo = ({ logo, name, colors, size = 'md' }) => {
  const dims = size === 'lg' ? 'w-16 h-16 sm:w-20 sm:h-20 text-2xl' : size === 'sm' ? 'w-9 h-9 text-sm' : 'w-12 h-12 text-lg';
  const bg = colors?.primary || '#1a1a2e';
  if (logo) {
    return (
      <img
        src={logo}
        alt={name}
        className={`${dims} rounded-lg object-cover shrink-0`}
        loading="lazy"
      />
    );
  }
  return (
    <div
      className={`${dims} rounded-lg flex items-center justify-center text-white font-heading uppercase shrink-0`}
      style={{ background: bg }}
    >
      {name?.charAt(0) || '?'}
    </div>
  );
};

const TrophyRow = ({ wins, color, size = 14 }) => {
  const shown = Math.min(wins, 5);
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: shown }).map((_, i) => (
        <Trophy key={i} size={size} style={{ color }} className="drop-shadow" />
      ))}
      {wins > 5 && (
        <span className="text-[11px] font-bold ml-0.5" style={{ color }}>+{wins - 5}</span>
      )}
    </div>
  );
};

const TournamentChip = ({ t, isItalian }) => {
  const typeLabel = t.tournament_type === 'knockout'
    ? (isItalian ? 'Eliminazione' : 'Knockout')
    : (isItalian ? 'Ibrido' : 'Hybrid');
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#0a0d15] border border-white/10 rounded text-[10px] text-slate-300"
      title={`${t.name}${t.season ? ' · ' + t.season : ''} · ${typeLabel}`}
      data-testid={`albodoro-tournament-chip-${t.championship_id}`}
    >
      <Trophy size={10} className="text-[#FFEA00]" />
      <span className="max-w-[140px] truncate">{t.name}</span>
      {t.season && <span className="text-slate-500">· {t.season}</span>}
    </span>
  );
};

const PodiumCard = ({ club, place, isItalian }) => {
  const cfg = PODIUM[place];
  const Icon = cfg.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.08 * place, type: 'spring', stiffness: 120 }}
      className="flex flex-col w-full"
      data-testid={`albodoro-podium-${place}`}
    >
      <div className={`relative flex flex-col items-center px-4 pt-7 pb-5 bg-[#0a0d15]/90 border ${cfg.ring} rounded-t-xl ${cfg.glow}`}>
        {/* Rank badge */}
        <div className={`absolute -top-3.5 left-1/2 -translate-x-1/2 px-3 py-1 ${cfg.badge} border rounded-full text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 backdrop-blur-sm bg-[#0a0d15]`}>
          <Icon size={12} />
          {cfg.rank} · {cfg.label[isItalian ? 'it' : 'en']}
        </div>

        {place === 1 && (
          <Crown size={22} className="text-[#FFD700] mb-1.5 animate-pulse" />
        )}

        <ClubLogo logo={club.club_logo} name={club.club_name} colors={club.club_colors} size="lg" />

        <p className="mt-3 font-heading text-white text-sm sm:text-base text-center uppercase tracking-wider line-clamp-2 min-h-[2.5rem] flex items-center">
          {club.club_name}
        </p>

        <div className="mt-1.5">
          <TrophyRow wins={club.wins} color={cfg.color} size={place === 1 ? 16 : 13} />
        </div>

        <div className={`mt-2 px-3 py-1 ${cfg.badge} border rounded-full text-[11px] font-bold`}>
          {club.wins} {club.wins === 1 ? (isItalian ? 'trofeo' : 'trophy') : (isItalian ? 'trofei' : 'trophies')}
        </div>

        {club.legacy_wins > 0 && (
          <div className="mt-1.5 px-2 py-0.5 bg-[#FF00E5]/10 border border-[#FF00E5]/30 text-[#FF00E5] rounded text-[9px] uppercase tracking-widest font-bold">
            {isItalian
              ? `${club.legacy_wins} storic${club.legacy_wins === 1 ? 'o' : 'i'}`
              : `${club.legacy_wins} historic`}
          </div>
        )}

        {club.tournaments.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1 justify-center">
            {club.tournaments.slice(0, 2).map((t) => (
              <TournamentChip key={t.championship_id} t={t} isItalian={isItalian} />
            ))}
            {club.tournaments.length > 2 && (
              <span className="px-2 py-0.5 bg-[#0a0d15] border border-white/10 rounded text-[10px] text-slate-400">
                +{club.tournaments.length - 2}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Pedestal base (desktop podium effect) */}
      <div className={`hidden md:block w-full ${cfg.baseH} bg-gradient-to-b ${cfg.base} border-x border-b ${cfg.ring} rounded-b-xl relative overflow-hidden`}>
        <span
          className="absolute inset-0 flex items-center justify-center font-heading text-2xl opacity-30 select-none"
          style={{ color: cfg.color }}
        >
          {place}
        </span>
      </div>
    </motion.div>
  );
};

const RankBadge = ({ pos }) => (
  <span className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-[11px] font-mono text-slate-400 shrink-0">
    {pos}°
  </span>
);

const AlboDoro = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isItalian = isItalianLang(user?.language);
  const [data, setData] = useState({ clubs: [], total_tournaments: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const isSuperAdmin = user?.role === 'super_admin';

  const fetchData = React.useCallback(async () => {
    try {
      const res = await championshipsApi.alboDoro();
      setData(res.data || { clubs: [], total_tournaments: 0 });
    } catch (err) {
      setError(err?.response?.data?.detail || (isItalian ? 'Errore caricamento' : 'Loading error'));
    } finally {
      setLoading(false);
    }
  }, [isItalian]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const top3 = data.clubs.slice(0, 3);
  const rest = data.clubs.slice(3);
  const totalTrophies = data.clubs.reduce((s, c) => s + (c.wins || 0), 0);

  return (
    <div className="min-h-screen ept-bg-ambient text-white">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-8" data-testid="albodoro-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-white/5 pb-4">
          <button
            className="flex items-center gap-2 text-slate-300 hover:text-white text-sm transition-colors"
            onClick={() => navigate(-1)}
            data-testid="albodoro-back-btn"
          >
            <ArrowLeft size={16} />
            {isItalian ? 'Indietro' : 'Back'}
          </button>
          <div className="flex items-center gap-3">
            {isSuperAdmin && (
              <button
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FFD700]/10 border border-[#FFD700]/30 text-[#FFD700] rounded-lg text-xs font-bold hover:bg-[#FFD700]/20 transition-colors"
                onClick={() => setManageOpen(true)}
                data-testid="albodoro-manage-btn"
              >
                <Settings size={13} />
                {isItalian ? 'Gestisci' : 'Manage'}
              </button>
            )}
            <button
              className="text-slate-400 hover:text-white text-xs transition-colors"
              onClick={() => navigate('/tornei')}
              data-testid="albodoro-tornei-link"
            >
              {isItalian ? 'Tornei' : 'Tournaments'}
            </button>
          </div>
        </div>

        {/* Hero */}
        <div className="text-center space-y-3">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#FFD700]/10 border border-[#FFD700]/30 shadow-[0_0_40px_rgba(255,215,0,0.15)]"
          >
            <Trophy size={28} className="text-[#FFD700]" />
          </motion.div>
          <h1 className="font-heading text-2xl sm:text-3xl uppercase tracking-widest text-white">
            {isItalian ? "Albo d'Oro" : 'Hall of Fame'}
          </h1>
          <p className="text-slate-400 text-xs sm:text-sm max-w-md mx-auto">
            {isItalian
              ? 'I club che hanno alzato il trofeo nei tornei completati'
              : 'Clubs that lifted the trophy in completed tournaments'}
          </p>

          {/* Stats strip */}
          {!loading && data.clubs.length > 0 && (
            <div className="flex items-center justify-center gap-3 pt-1">
              <div className="px-3 py-1.5 bg-[#0a0d15]/80 border border-white/10 rounded-lg flex items-center gap-2">
                <Trophy size={13} className="text-[#FFEA00]" />
                <span className="text-xs text-slate-300">
                  <b className="text-white">{totalTrophies}</b> {isItalian ? 'trofei' : 'trophies'}
                </span>
              </div>
              <div className="px-3 py-1.5 bg-[#0a0d15]/80 border border-white/10 rounded-lg flex items-center gap-2">
                <Star size={13} className="text-[#FF00E5]" />
                <span className="text-xs text-slate-300">
                  <b className="text-white">{data.clubs.length}</b> {isItalian ? 'club premiati' : 'awarded clubs'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Loading / Empty / Error */}
        {loading && (
          <div className="flex items-center justify-center gap-2 py-16 text-slate-400">
            <Loader2 size={18} className="animate-spin" />
            {isItalian ? 'Calcolo classifica…' : 'Computing ranking…'}
          </div>
        )}

        {!loading && error && (
          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded text-red-300 text-sm text-center">
            {error}
          </div>
        )}

        {!loading && !error && data.clubs.length === 0 && (
          <div className="py-16 text-center text-slate-500 space-y-2" data-testid="albodoro-empty">
            <Trophy size={32} className="text-slate-700 mx-auto" />
            <p className="text-sm">
              {isItalian
                ? 'Ancora nessun torneo completato. Quando un torneo termina, il vincitore comparirà qui.'
                : 'No completed tournament yet. Winners will appear here once tournaments end.'}
            </p>
          </div>
        )}

        {/* Podium top-3: 2-1-3 on desktop, 1-2-3 stacked on mobile */}
        {!loading && top3.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-3 md:items-end pt-4 md:px-2">
            <div className="md:order-1 flex flex-col justify-end order-2">
              {top3[1] && <PodiumCard club={top3[1]} place={2} isItalian={isItalian} />}
            </div>
            <div className="md:order-2 flex flex-col justify-end order-1">
              {top3[0] && <PodiumCard club={top3[0]} place={1} isItalian={isItalian} />}
            </div>
            <div className="md:order-3 flex flex-col justify-end order-3">
              {top3[2] && <PodiumCard club={top3[2]} place={3} isItalian={isItalian} />}
            </div>
          </div>
        )}

        {/* Remaining ranked table */}
        {!loading && rest.length > 0 && (
          <div className="bg-[#0a0d15]/80 border border-white/10 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-white/10 bg-white/[0.02] flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-widest text-slate-300 font-bold flex items-center gap-2">
                <Medal size={14} className="text-[#FFEA00]" />
                {isItalian ? 'Altri club premiati' : 'Other awarded clubs'}
              </p>
              <p className="text-[10px] text-slate-500">
                {rest.length} club
              </p>
            </div>
            <div className="divide-y divide-white/5">
              {rest.map((club, idx) => (
                <motion.div
                  key={club.club_id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.015 * idx }}
                  className="flex items-center gap-3 px-3 sm:px-4 py-2.5 hover:bg-white/5 transition-colors"
                  data-testid={`albodoro-row-${club.club_id}`}
                >
                  <RankBadge pos={idx + 4} />
                  <ClubLogo logo={club.club_logo} name={club.club_name} colors={club.club_colors} size="sm" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium truncate">{club.club_name}</p>
                    {club.tournaments.length > 0 && (
                      <div className="hidden sm:flex flex-wrap gap-1 mt-0.5">
                        {club.tournaments.slice(0, 2).map((t) => (
                          <TournamentChip key={t.championship_id} t={t} isItalian={isItalian} />
                        ))}
                        {club.tournaments.length > 2 && (
                          <span className="px-2 py-0.5 bg-[#0a0d15] border border-white/10 rounded text-[10px] text-slate-400">
                            +{club.tournaments.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <TrophyRow wins={club.wins} color="#FFEA00" size={13} />
                  <span className="px-2.5 py-1 bg-[#FFEA00]/10 border border-[#FFEA00]/30 text-[#FFEA00] rounded-lg text-[11px] font-bold shrink-0 min-w-[36px] text-center">
                    {club.wins}×
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>

      {isSuperAdmin && (
        <AlboLegacyManager
          open={manageOpen}
          onOpenChange={setManageOpen}
          onChanged={fetchData}
          isItalian={isItalian}
        />
      )}
    </div>
  );
};

export default AlboDoro;
